/**
 * Pure mutation functions for PCB rules actions.
 */

import {handleSetNodeRules} from "@buildwithflux/core";
import type {IDocumentData, IPcbLayoutNodesRuleSetsMap, RuleKey} from "@buildwithflux/models";
import type {PcbRuleSetsMap} from "@buildwithflux/core";
import {guid} from "@buildwithflux/shared";
import {cloneDeep} from "lodash";

/**
 * Pure mutation function for setGlobalPcbRuleSet.
 */
export function applySetGlobalPcbRuleSet(
    draft: IDocumentData,
    setPcbLayoutRules: PcbRuleSetsMap,
    selectedGlobalPcbLayoutRule?: string,
): void {
    for (const pcbLayoutRuleSet of Object.values(setPcbLayoutRules)) {
        draft.pcbLayoutRuleSets[pcbLayoutRuleSet.uid] = pcbLayoutRuleSet;
    }
    if (selectedGlobalPcbLayoutRule) {
        draft.selectedObjectUids = [selectedGlobalPcbLayoutRule];
    }
}

/**
 * Pure mutation function for setNodeRules.
 */
export function applySetNodeRules(draft: IDocumentData, updatedNodes: IPcbLayoutNodesRuleSetsMap): void {
    handleSetNodeRules(draft, updatedNodes);
}

/**
 * Pure mutation function for removeNodeRules.
 */
export function applyRemoveNodeRules(draft: IDocumentData, nodeId: string, ruleKeys: RuleKey[]): void {
    const node = draft.pcbLayoutNodes[nodeId];
    if (node?.pcbNodeRuleSet) {
        for (const ruleKey of ruleKeys) {
            delete node.pcbNodeRuleSet[ruleKey as RuleKey];
        }
    }
}

/**
 * Pure mutation function for toggleGlobalPcbRule.
 */
export function applyToggleGlobalPcbRule(
    draft: IDocumentData,
    ruleId: string,
    state: boolean,
    subRuleId?: string,
): void {
    const rule = draft.pcbLayoutRuleSets[ruleId];
    if (rule) {
        if (!subRuleId) {
            rule.disabled = state;
            for (const subRule of Object.values(rule.rules)) {
                subRule.disabled = rule.disabled;
            }
        } else {
            const subRule = Object.values(rule.rules).find((r) => r.uid === subRuleId);
            if (subRule) {
                subRule.disabled = state;
                rule.disabled = Object.values(rule.rules).every((sr) => sr.disabled);
            }
        }
    }
}

/**
 * Pure mutation function for toggleNodeSpecificRule.
 */
export function applyToggleNodeSpecificRule(
    draft: IDocumentData,
    ruleId: string,
    nodeId: string,
    toggleState: boolean,
): void {
    const node = draft.pcbLayoutNodes[nodeId];
    if (node) {
        const rule = Object.values(node.pcbNodeRuleSet).find((r) => r.uid === ruleId);
        if (rule) {
            rule.disabled = toggleState;
        }
    }
}

/**
 * Pure mutation function for duplicateGlobalPcbRuleSet.
 */
export function applyDuplicateGlobalPcbRuleSet(draft: IDocumentData, ruleUids: string[]): void {
    for (const ruleUid of ruleUids) {
        const existing = draft.pcbLayoutRuleSets[ruleUid];
        if (existing) {
            const newUid = guid();
            const cloned = cloneDeep(existing);

            let newName = cloned.name;
            if (newName !== undefined) {
                const existingRulesetNames: string[] = [];
                for (const ruleset of Object.values(draft.pcbLayoutRuleSets)) {
                    if (ruleset.name) {
                        existingRulesetNames.push(ruleset.name);
                    }
                }
                existingRulesetNames.push(`${newName} 1`);
                newName = getNextAvailableName(existingRulesetNames, newName);
            }

            const newRules: typeof cloned.rules = {};
            for (const ruleKey of Object.keys(cloned.rules)) {
                const typedKey = ruleKey as RuleKey;
                const subRule = cloned.rules[typedKey];
                if (subRule) {
                    newRules[typedKey] = {...subRule, uid: guid()};
                }
            }

            draft.pcbLayoutRuleSets[newUid] = {
                ...cloned,
                uid: newUid,
                name: newName,
                rules: newRules,
            };
        }
    }
}

/**
 * Pure mutation function for removeGlobalPcbRuleSet.
 */
export function applyRemoveGlobalPcbRuleSet(draft: IDocumentData, pcbLayoutRuleUids: string[]): void {
    for (const ruleUid of pcbLayoutRuleUids) {
        delete draft.pcbLayoutRuleSets[ruleUid];
    }
}

function getNextAvailableName(existingNames: string[], baseName: string): string {
    let counter = 1;
    let newName = `${baseName} ${counter}`;
    while (existingNames.includes(newName)) {
        counter++;
        newName = `${baseName} ${counter}`;
    }
    return newName;
}
